/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/decay.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Calculates a relevance score that decays based on the distance of a numeric, spatial or date type value from a target origin, using configurable decay functions.
 *
 * @example
 * FROM employees
 * | EVAL decay_result = decay(salary, 0, 100000, {"offset": 5, "decay": 0.5, "type": "linear"})
 * | SORT decay_result DESC
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'decay',
  signatures: [
    {
      params: [
        {
          name: 'value',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'origin',
          type: 'cartesian_point',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'value',
          type: 'date',
          optional: false,
        },
        {
          name: 'origin',
          type: 'date',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'value',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'origin',
          type: 'date_nanos',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'value',
          type: 'double',
          optional: false,
        },
        {
          name: 'origin',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'value',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'origin',
          type: 'geo_point',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'keyword',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'value',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'origin',
          type: 'geo_point',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'text',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'value',
          type: 'integer',
          optional: false,
        },
        {
          name: 'origin',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'value',
          type: 'long',
          optional: false,
        },
        {
          name: 'origin',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'value',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'origin',
          type: 'unsigned_long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'scale',
          type: 'unsigned_long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'offset\', values=[], description=\'Distance from the origin where no decay occurs.\', type=[double, integer, long, unsigned_long, time_duration, keyword, text]}, {name=\'decay\', values=[], description=\'Multiplier value returned at the scale distance from the origin.\', type=[double]}, {name=\'type\', values=[], description=\'Decay function to use: linear, exponential or gaussian.\', type=[keyword]}',
        },
      ],
      variadic: false,
      returnType: 'double',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
